/**
 * Repeat Task Utilities
 * Handles recurring task logic for creating next instances
 */

export interface RepeatPattern {
  freq: string
  interval?: number
  byday?: string
  label: string
}

/**
 * Parse repeat pattern from task name or tags
 * Supports #repeat/pattern format (Roam/Workflowy) or repeat object
 */
export function parseRepeatPattern(task: { name?: string; tags?: string[]; repeat?: any }): string | null {
  // Check if repeat is already parsed (from database)
  if (task.repeat) {
    // If it's an object with pattern property
    if (typeof task.repeat === 'object' && task.repeat.pattern) {
      return task.repeat.pattern
    }
    // If it's a string
    if (typeof task.repeat === 'string') {
      return task.repeat
    }
  }

  // Parse from name
  if (task.name) {
    const match = task.name.match(/#repeat\/(\w+)/i)
    if (match) {
      return match[1].toLowerCase()
    }
  }

  // Parse from tags
  if (task.tags) {
    for (const tag of task.tags) {
      const match = tag.match(/repeat\/(\w+)/i)
      if (match) {
        return match[1].toLowerCase()
      }
    }
  }

  return null
}

/**
 * Parse repeat ID from task name or tags
 * Format: #r/{id}
 */
export function parseRepeatId(task: { name?: string; tags?: string[]; repeatId?: string }): string | null {
  // Check database repeatId first
  if (task.repeatId) {
    return task.repeatId
  }

  // Parse from name
  if (task.name) {
    const match = task.name.match(/#r\/([a-z0-9]+)/i)
    if (match) {
      return match[0] // Return full #r/xxx format
    }
  }

  // Parse from tags
  if (task.tags) {
    for (const tag of task.tags) {
      const match = tag.match(/r\/([a-z0-9]+)/i)
      if (match) {
        return `#r/${match[1]}`
      }
    }
  }

  return null
}

/**
 * Calculate next date based on repeat pattern
 */
export function calculateNextDate(currentDate: string | null, pattern: string): string | null {
  if (!currentDate || !pattern) return null

  const [year, month, day] = currentDate.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const dayMap: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  }

  switch (pattern.toLowerCase()) {
    case 'daily':
      date.setDate(date.getDate() + 1)
      break
    case 'weekdays':
      do {
        date.setDate(date.getDate() + 1)
      } while (date.getDay() === 0 || date.getDay() === 6)
      break
    case 'weekly':
      date.setDate(date.getDate() + 7)
      break
    case 'biweekly':
      date.setDate(date.getDate() + 14)
      break
    case 'monthly':
      date.setMonth(date.getMonth() + 1)
      break
    case 'yearly':
      date.setFullYear(date.getFullYear() + 1)
      break
    case 'mon':
    case 'tue':
    case 'wed':
    case 'thu':
    case 'fri':
    case 'sat':
    case 'sun':
    case 'monday':
    case 'tuesday':
    case 'wednesday':
    case 'thursday':
    case 'friday':
    case 'saturday':
    case 'sunday':
      const shortDay = pattern.slice(0, 3).toLowerCase()
      const targetDay = dayMap[shortDay]
      do {
        date.setDate(date.getDate() + 1)
      } while (date.getDay() !== targetDay)
      break
    default:
      return null
  }

  const newYear = date.getFullYear()
  const newMonth = String(date.getMonth() + 1).padStart(2, '0')
  const newDay = String(date.getDate()).padStart(2, '0')
  return `${newYear}-${newMonth}-${newDay}`
}

/**
 * Generate a unique repeat ID
 * Format: #r/xxxxxxxx
 */
export function generateRepeatId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let id = ''
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return `#r/${id}`
}

/**
 * Get today's date as YYYY-MM-DD
 */
export function getTodayString(): string {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
